import { useNavigate } from 'react-router-dom'
import { Container, Stack, Text, Paper, Group, Box, Loader, Button, Badge, Progress } from '@mantine/core'
import { IconArrowLeft, IconBrain, IconChartRadar, IconRefresh, IconRocket } from '@tabler/icons-react'
import { useUserAnalysis } from '../hooks/useUserAnalysis'
import { AdaptScoreRing } from '../components/analysis/AdaptScoreRing'
import { LivingSkillCard } from '../components/analysis/LivingSkillCard'
import { SkillIndicatorGrid } from '../components/analysis/SkillIndicatorGrid'
import { MethodologySection } from '../components/analysis/MethodologySection'
import { EvolutionTimeline } from '../components/analysis/EvolutionTimeline'
import { SectionHeader } from '../components/analysis/SectionHeader'
import { MarkdownRenderer } from '../components/common/MarkdownRenderer'

/* ─── Confidence Indicator ─── */

function ConfidenceIndicator({ level }) {
  const configs = {
    low: { color: 'orange', label: 'Early Data' },
    moderate: { color: 'yellow', label: 'Growing Confidence' },
    high: { color: 'green', label: 'Strong Data' },
  }
  const config = configs[level] || configs.low

  return (
    <Badge size="xs" variant="light" color={config.color}>
      {config.label}
    </Badge>
  )
}

/* ─── Insufficient Data State ─── */

function InsufficientDataState({ analysis, navigate }) {
  const sessionsNeeded = analysis?.sessions_needed || 2
  const totalSessions = analysis?.data_summary?.total_sessions || 0

  return (
    <Stack align="center" gap="lg" py={40} maw={440} mx="auto">
      <IconBrain
        size={48}
        color="var(--mantine-color-cyan-4)"
        style={{ opacity: 0.6 }}
      />

      <Text fw={600} ff="Orbitron, sans-serif" c="cyan.3" ta="center">
        Building Your Profile
      </Text>

      <Text c="dimmed" size="sm" ta="center" lh={1.6}>
        Your profile analysis needs data from multiple stories to provide meaningful insights.
        Complete {sessionsNeeded} more {sessionsNeeded === 1 ? 'story' : 'stories'} to unlock your profile.
      </Text>

      {/* Progress indicator */}
      <Paper p="md" className="glass" w="100%">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="xs" c="dimmed">Stories completed</Text>
            <Text size="xs" c="cyan.3" fw={600}>{totalSessions} / 2</Text>
          </Group>
          <Progress
            value={(totalSessions / 2) * 100}
            color="cyan"
            size="sm"
            radius="xl"
          />
        </Stack>
      </Paper>

      <Button
        leftSection={<IconRocket size={16} />}
        onClick={() => navigate('/')}
      >
        Browse Stories
      </Button>
    </Stack>
  )
}

/* ─── Profile Content ─── */

function ProfileContent({ analysis }) {
  const sufficiency = analysis.data_summary?.sufficiency_level || 'minimal'
  const aggregatedProfile = analysis.aggregated_profile || {}
  const confidence = aggregatedProfile.confidence || sufficiency

  const hasAdaptScores = sufficiency !== 'minimal' && analysis.adapt_scores
  const hasLivingSkills = sufficiency !== 'minimal' && analysis.living_skills?.length > 0
  const hasMethodology = sufficiency !== 'minimal' && analysis.methodology_patterns
  const hasSkillIndicators = sufficiency === 'strong' && analysis.skill_indicators?.length > 0
  const hasObjectivePatterns = sufficiency !== 'minimal' && analysis.learning_objective_patterns?.length > 0

  return (
    <Stack gap="lg">
      {/* Header */}
      <Paper
        p="lg"
        className="glass"
        style={{
          borderColor: 'rgba(0, 229, 255, 0.2)',
          textAlign: 'center',
        }}
      >
        <Text
          size="xs"
          c="dimmed"
          ff="Orbitron, sans-serif"
          fw={600}
          tt="uppercase"
          lts={2}
          mb={4}
        >
          Your Profile Analysis
        </Text>
        <Group justify="center" gap="sm" mt={4}>
          <Text size="xs" c="dimmed">
            {analysis.data_summary?.total_sessions || 0} stories, {analysis.data_summary?.total_decisions || 0} decisions
          </Text>
          <ConfidenceIndicator level={confidence} />
        </Group>
        {aggregatedProfile.confidence_rationale && (
          <Text size="xs" c="dimmed" mt={4} fs="italic">
            {aggregatedProfile.confidence_rationale}
          </Text>
        )}
      </Paper>

      {/* ADAPT Scores (moderate+) */}
      {hasAdaptScores && (
        <Paper p="lg" className="glass">
          <Group justify="space-between" mb="md">
            <Text
              size="xs"
              c="dimmed"
              ff="Orbitron, sans-serif"
              fw={600}
              tt="uppercase"
              lts={1}
            >
              ADAPT Framework
            </Text>
            <ConfidenceIndicator level={confidence} />
          </Group>
          <AdaptScoreRing mode="scores" scores={analysis.adapt_scores} />
        </Paper>
      )}

      {/* Living Skills (moderate+) */}
      {hasLivingSkills && (
        <Box>
          <SectionHeader>Living Skills</SectionHeader>
          <Stack gap="sm">
            {analysis.living_skills.map((skill, i) => (
              <LivingSkillCard key={i} skill={skill} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Learning Objective Patterns (moderate+) */}
      {hasObjectivePatterns && (
        <Box>
          <SectionHeader>Learning Objective Patterns</SectionHeader>
          <Stack gap="xs">
            {analysis.learning_objective_patterns.map((pattern, i) => (
              <Paper key={i} p="sm" className="glass">
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm" c="gray.3" fw={600}>{pattern.theme}</Text>
                  <Group gap="xs">
                    <Badge size="xs" variant="light" color="cyan">
                      {pattern.frequency}x
                    </Badge>
                    <Badge
                      size="xs"
                      variant="light"
                      color={pattern.trend === 'improving' ? 'green' : pattern.trend === 'declining' ? 'red' : 'gray'}
                      tt="capitalize"
                    >
                      {pattern.trend}
                    </Badge>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Methodology Patterns (moderate+) */}
      {hasMethodology && (
        <Box>
          <SectionHeader>Methodology Patterns</SectionHeader>
          <MethodologySection insights={analysis.methodology_patterns} />
        </Box>
      )}

      {/* Skill Indicators (strong only) */}
      {hasSkillIndicators && (
        <Box>
          <SectionHeader>Skill Indicators</SectionHeader>
          <SkillIndicatorGrid indicators={analysis.skill_indicators} />
        </Box>
      )}

      {/* Evolution Timeline */}
      {analysis.evolution?.length > 0 && (
        <Box>
          <SectionHeader>Your Journey</SectionHeader>
          <Paper p="lg" className="glass">
            <EvolutionTimeline evolution={analysis.evolution} />
          </Paper>
        </Box>
      )}

      {/* Evolution Narrative */}
      {analysis.evolution_narrative && (
        <Paper p="lg" className="glass">
          <Text
            size="xs"
            c="dimmed"
            ff="Orbitron, sans-serif"
            fw={600}
            tt="uppercase"
            lts={1}
            mb="md"
          >
            Growth Patterns
          </Text>
          <MarkdownRenderer content={analysis.evolution_narrative} />
        </Paper>
      )}

      {/* Profile Narrative */}
      {analysis.profile_narrative && (
        <Paper p="lg" className="glass">
          <Text
            size="xs"
            c="dimmed"
            ff="Orbitron, sans-serif"
            fw={600}
            tt="uppercase"
            lts={1}
            mb="md"
          >
            Your Profile
          </Text>
          <MarkdownRenderer content={analysis.profile_narrative} />
        </Paper>
      )}
    </Stack>
  )
}

/* ─── Main Page ─── */

export default function UserProfileAnalysis() {
  const navigate = useNavigate()
  const { analysis, isLoading, isProcessing, needsUpgrade, error, refetch } = useUserAnalysis()

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Loader size="md" color="cyan" />
          <Text c="dimmed" size="sm">Loading your profile analysis...</Text>
        </Stack>
      </Container>
    )
  }

  if (needsUpgrade) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60} maw={440} mx="auto">
          <IconBrain size={40} color="var(--mantine-color-cyan-4)" />
          <Text fw={600} ff="Orbitron, sans-serif" c="cyan.3">
            Profile Locked
          </Text>
          <Text c="dimmed" size="sm" ta="center">
            Create a full account to access your profile analysis.
          </Text>
          <Group gap="sm">
            <Button
              variant="light"
              color="gray"
              size="sm"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate('/profile')}
            >
              Back to Profile
            </Button>
            <Button size="sm" onClick={() => navigate('/settings')}>
              Create Account
            </Button>
          </Group>
        </Stack>
      </Container>
    )
  }

  if (isProcessing) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60} maw={440} mx="auto">
          <Loader size="md" color="cyan" />
          <Text fw={600} ff="Orbitron, sans-serif" c="cyan.3">
            Generating Your Profile
          </Text>
          <Text c="dimmed" size="sm" ta="center">
            Analyzing your cross-session data...
          </Text>
        </Stack>
      </Container>
    )
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Text c="red.4" fw={600} ff="Orbitron, sans-serif">
            Profile Unavailable
          </Text>
          <Text c="dimmed" size="sm" ta="center" maw={400}>
            Unable to load your profile analysis. Please try again.
          </Text>
          <Group gap="sm">
            <Button
              variant="light"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/profile')}
            >
              Back to Profile
            </Button>
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </Group>
        </Stack>
      </Container>
    )
  }

  // Determine if insufficient data
  const isInsufficient = analysis?.data_summary?.sufficiency_level === 'insufficient'

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/profile')}
          style={{ alignSelf: 'flex-start' }}
          size="sm"
        >
          Back to Profile
        </Button>

        {isInsufficient ? (
          <InsufficientDataState analysis={analysis} navigate={navigate} />
        ) : analysis ? (
          <ProfileContent analysis={analysis} />
        ) : (
          <InsufficientDataState analysis={{}} navigate={navigate} />
        )}
      </Stack>
    </Container>
  )
}
